import { useCallback, useEffect, useMemo } from 'react';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { useContent } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import {
  filterPoSelectionIds,
  filterTgSelectionIds,
  parsePoSelectionId,
  parseTgSelectionId,
} from 'src/contexts/MultiSelectionContext/MultiSelectionContext.utils';
import {
  BulkAdjustCostDialog,
  CostAdjustmentType,
} from 'src/dialogs/BulkEdits/BulkAdjustCostDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkAdjustCostField,
  BulkChangePriceMode,
  BulkEditPreviewWithDetails,
  BulkEditPurchaseClient,
  BulkEditTicketGroupClient,
  CommandOptionType,
  Feature,
  PurchaseOrderQuery,
} from 'src/WebApiController';

import { PURCHASE_BULK_ADJUST_COST_UPDATE_KEY } from '../../PurchaseActionDropdown.constants';
import { LaunchBulkAdjustCost } from './LaunchBulkAdjustCost';
import { LaunchBulkAdjustPurchaseTotalCost } from './LaunchBulkAdjustPurchaseTotalCost';
import { LaunchBulkAdjustPurchaseUnitCost } from './LaunchBulkAdjustPurchaseUnitCost';
import { LaunchBulkAdjustTicketGroupUnitCost } from './LaunchBulkAdjustTicketGroupUnitCost';

export const useBulkAdjustCost = (
  filterQuery: PurchaseOrderQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  groupId?: string,
  isEventView?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = groupId
    ? `bulkAdjustCost-event-${groupId}`
    : PURCHASE_BULK_ADJUST_COST_UPDATE_KEY;
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const hasBulkSetPurchaseOrderCostFeature = useUserHasFeature(
    Feature.BulkSetPurchaseOrderCost
  );

  // Tooltip messages for disabled menu items
  const purchaseCostDisabledTooltip = useContent(
    ContentId.AdjustPurchaseCostDisabledTooltip
  );
  const tgCostDisabledTooltip = useContent(
    ContentId.AdjustTicketGroupCostDisabledTooltip
  );
  const mixedSelectionDisabledTooltip = useContent(
    ContentId.AdjustCostMixedSelectionDisabledTooltip
  );

  // Get raw selection IDs to determine selection type
  const { getSelection, totalItemsSelected } = useMultiSelectionContext();

  // Determine selection type based on raw selection IDs.
  //
  // When you click a PO row checkbox, TanStack Table cascades and selects all
  // TG sub-rows too. But the raw selection IDs tell us the truth:
  // - PO selection IDs are simple numbers without commas (e.g., "12345")
  // - TG selection IDs are packed format with commas (e.g., "12345,67890")
  //
  // Selection types:
  // 1. Pure PO selection: user selected only PO rows (IDs without commas, plus cascaded TG IDs)
  // 2. Pure TG selection: user selected only TG sub-rows (ALL IDs have commas)
  // 3. Mixed selection: user selected both PO rows AND separate TG sub-rows from other POs
  //
  // To detect mixed selection: check if there are TG IDs whose parent PO is NOT in the selection.
  // When you click a PO row, the PO ID and all its child TG IDs are added together.
  // If a TG ID exists without its parent PO ID, the user must have clicked that TG row separately.
  const { hasPurePoSelection, hasPureTgSelection, hasMixedSelection } =
    useMemo(() => {
      const selection = getSelection();
      const rawItemIds = selection.items.itemIds;

      if (rawItemIds.length === 0) {
        return {
          hasPurePoSelection: false,
          hasPureTgSelection: false,
          hasMixedSelection: false,
        };
      }

      // Separate PO IDs (no comma) from TG IDs (has comma)
      const poIds = new Set(
        filterPoSelectionIds(rawItemIds).map(parsePoSelectionId)
      );

      const tgIdsWithParent =
        filterTgSelectionIds(rawItemIds).map(parseTgSelectionId);

      // No PO rows selected - check if this is pure TG selection
      if (poIds.size === 0) {
        return {
          hasPurePoSelection: false,
          hasPureTgSelection: tgIdsWithParent.length > 0,
          hasMixedSelection: false,
        };
      }

      // PO rows were selected - check if any TG's parent PO is NOT in the selection
      // If so, user selected TG rows separately (mixed selection)
      const hasOrphanTg = tgIdsWithParent.some((tg) => !poIds.has(tg.poId));

      if (hasOrphanTg) {
        // Mixed selection: some TGs don't belong to selected POs
        return {
          hasPurePoSelection: false,
          hasPureTgSelection: false,
          hasMixedSelection: true,
        };
      }

      // All TGs belong to selected POs - this is pure PO selection (with cascade)
      return {
        hasPurePoSelection: true,
        hasPureTgSelection: false,
        hasMixedSelection: false,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getSelection, totalItemsSelected]); // totalItemsSelected triggers re-computation when selection changes

  const {
    openDialog: openBulkAdjustCostDialog,
    closeDialog: closeBulkAdjustCostDialog,
  } = useDialog(DialogId.BulkAdjustCost, BulkAdjustCostDialog);

  const onSubmit = useCallback(
    async (
      adjustmentPercent: number | undefined,
      adjustmentAbsoluteAmount: number | undefined,
      changeMode: BulkChangePriceMode,
      targetField: BulkAdjustCostField,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const client = isEventView
              ? new BulkEditTicketGroupClient(activeAccountWebClientConfig)
              : new BulkEditPurchaseClient(activeAccountWebClientConfig);
            const preview = await client.bulkAdjustCostPreview(filterQuery);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkAdjustCostPreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            ).bulkAdjustCost(
              preview!.preview,
              changeMode,
              targetField,
              adjustmentPercent,
              adjustmentAbsoluteAmount,
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkAdjustCostDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitAdjustCost', error, {
              trackErrorData: {
                changeMode,
                targetField,
                adjustmentPercent,
                adjustmentAbsoluteAmount,
                preview,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      isEventView,
      activeAccountWebClientConfig,
      filterQuery,
      showErrorDialog,
      updateKey,
      closeBulkAdjustCostDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  // Create launch action for a specific cost adjustment type
  const createLaunchAction = useCallback(
    (costAdjustmentType: CostAdjustmentType) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkAdjustCostDialog({
        updateKey,
        isEventView,
        costAdjustmentType,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkAdjustCostDialog,
      });
    },
    [
      closeBulkAdjustCostDialog,
      isEventView,
      onSubmit,
      openBulkAdjustCostDialog,
      setIsLoading,
      updateKey,
    ]
  );

  // Legacy action (for backward compatibility with command menu)
  const onLaunchAction = useMemo(
    () => createLaunchAction('ticketGroupUnitCost'),
    [createLaunchAction]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkAdjustCostDialog({
      updateKey,
      isEventView,
      costAdjustmentType: 'ticketGroupUnitCost',
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkAdjustCostDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.PurchasesBulkAdjustCost,
        onSelect: openDialogStableRef,
      },
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.PurchasesBulkAdjustCost,
      });
    };
  }, [openDialogStableRef, registerCommand, unregisterCommand]);

  // Determine which action items to show based on selection type and feature flag (for mobile view)
  const actionItems = useMemo<BulkActionItem[]>(() => {
    // For event view: Only show TG option
    if (isEventView) {
      return [
        {
          title: ContentId.AdjustTicketGroupUnitCost,
          onClick: createLaunchAction('ticketGroupUnitCost'),
          disabled: isLoading,
        },
      ];
    }

    // For time view with feature flag: Show all options with appropriate disabled states
    if (hasBulkSetPurchaseOrderCostFeature) {
      // PO options enabled only when pure PO selection
      // TG option enabled only when pure TG selection
      // All disabled when mixed selection
      return [
        {
          title: ContentId.AdjustPurchaseUnitCost,
          onClick: createLaunchAction('purchaseOrderUnitCost'),
          disabled: isLoading || !hasPurePoSelection,
        },
        {
          title: ContentId.AdjustPurchaseTotalCost,
          onClick: createLaunchAction('purchaseOrderTotalCost'),
          disabled: isLoading || !hasPurePoSelection,
        },
        {
          title: ContentId.AdjustTicketGroupUnitCost,
          onClick: createLaunchAction('ticketGroupUnitCost'),
          disabled: isLoading || !hasPureTgSelection,
        },
      ];
    }

    // Fallback: Show legacy "Adjust Purchase Cost" menu item
    return [
      {
        title: ContentId.AdjustPurchaseCost,
        onClick: onLaunchAction,
        disabled: isLoading,
      },
    ];
  }, [
    isEventView,
    hasBulkSetPurchaseOrderCostFeature,
    hasPurePoSelection,
    hasPureTgSelection,
    createLaunchAction,
    isLoading,
    onLaunchAction,
  ]);

  // Helper to get the appropriate tooltip for PO options
  const getPoOptionsTooltip = () => {
    if (hasMixedSelection) return mixedSelectionDisabledTooltip;
    if (hasPureTgSelection) return purchaseCostDisabledTooltip;
    return undefined;
  };

  // Helper to get the appropriate tooltip for TG option
  const getTgOptionTooltip = () => {
    if (hasMixedSelection) return mixedSelectionDisabledTooltip;
    if (hasPurePoSelection) return tgCostDisabledTooltip;
    return undefined;
  };

  // Determine which dropdown items to show based on selection type and feature flag
  const dropDownItems = useMemo(() => {
    // For event view: Only show TG option
    if (isEventView) {
      return (
        <LaunchBulkAdjustTicketGroupUnitCost
          key="bulkAdjustTicketGroupUnitCost"
          onClick={createLaunchAction('ticketGroupUnitCost')}
          disabled={isLoading}
        />
      );
    }

    // For time view with feature flag: Show all options with appropriate disabled states and tooltips
    if (hasBulkSetPurchaseOrderCostFeature) {
      // PO options enabled only when pure PO selection
      // TG option enabled only when pure TG selection
      // All disabled when mixed selection (with mixed selection tooltip)
      const poOptionsDisabled = isLoading || !hasPurePoSelection;
      const tgOptionDisabled = isLoading || !hasPureTgSelection;

      return (
        <>
          <LaunchBulkAdjustPurchaseUnitCost
            key="bulkAdjustPurchaseUnitCost"
            onClick={createLaunchAction('purchaseOrderUnitCost')}
            disabled={poOptionsDisabled}
            disabledTooltip={
              poOptionsDisabled ? getPoOptionsTooltip() : undefined
            }
          />
          <LaunchBulkAdjustPurchaseTotalCost
            key="bulkAdjustPurchaseTotalCost"
            onClick={createLaunchAction('purchaseOrderTotalCost')}
            disabled={poOptionsDisabled}
            disabledTooltip={
              poOptionsDisabled ? getPoOptionsTooltip() : undefined
            }
          />
          <LaunchBulkAdjustTicketGroupUnitCost
            key="bulkAdjustTicketGroupUnitCost"
            onClick={createLaunchAction('ticketGroupUnitCost')}
            disabled={tgOptionDisabled}
            disabledTooltip={
              tgOptionDisabled ? getTgOptionTooltip() : undefined
            }
          />
        </>
      );
    }

    // Fallback: Show legacy "Adjust Purchase Cost" menu item
    return (
      <LaunchBulkAdjustCost
        key="bulkAdjustCost"
        onClick={onLaunchAction}
        disabled={isLoading}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEventView,
    hasBulkSetPurchaseOrderCostFeature,
    hasPurePoSelection,
    hasPureTgSelection,
    hasMixedSelection,
    createLaunchAction,
    isLoading,
    onLaunchAction,
    purchaseCostDisabledTooltip,
    tgCostDisabledTooltip,
    mixedSelectionDisabledTooltip,
  ]);

  return {
    dropDown: dropDownItems,
    actionItems,
  };
};
